import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { api } from "../api";
import type { PlmProductInfo, BundleSummary, BundleMember, ProductsResponse, EnvironmentsResponse } from "../types";
import {
  LEVEL_ICONS,
  patchTenant,
  patchInstance,
  patchPlmProduct,
  removeTenant,
  removeInstance,
  removePlmProduct,
  type TreeTenant,
  type TreeInstance,
  type TreePlmProduct,
} from "./deployments/tree-model";

import "@bee/pattern-library";
import "../components/create-tenant-dialog";
import "../components/create-instance-dialog";
import "../components/create-plm-product-dialog";
import "../components/create-bundle-dialog";
import "../components/edit-plm-product-dialog";
import "../components/confirm-delete-dialog";

type DeleteCtx = {
  entityType: string;
  name: string;
  hint?: string;
  action: () => Promise<{ prUrl?: string; prNumber?: number } | void>;
  onDeleted: () => void;
};

@customElement("deployments-page")
export class DeploymentsPage extends LitElement {
  static styles = css`
    :host {
      display: block;
      font-family: Geist, sans-serif;
    }

    .controls {
      display: flex;
      align-items: center;
      gap: var(--spacing-12, 12px);
      margin-bottom: var(--spacing-16, 16px);
    }

    .tree-wrap {
      margin-top: var(--spacing-8, 8px);
      border: 1px solid var(--colors-border-border-gray-200, #eaecf0);
      border-radius: var(--radius-8, 8px);
      padding: var(--spacing-8, 8px);
      background: var(--colors-background-bg-primary, #fff);
    }

    bee-folder-tree {
      --indent-guide-width: 1px;
      --indent-guide-color: var(--colors-border-border-gray-200, #eaecf0);
      --indent-guide-style: solid;
    }

    .node-name {
      font-size: 13px;
    }

    .node-name.mono {
      font-family: monospace;
    }

    .node-meta {
      font-size: 11px;
      color: var(--colors-foreground-fg-gray-500, #98a2b3);
      margin-left: var(--spacing-8, 8px);
    }

    .level-icon {
      color: var(--colors-foreground-fg-gray-500, #98a2b3);
    }

    .node-actions {
      display: flex;
      align-items: center;
      gap: var(--spacing-4, 4px);
    }

    .bundle-leaf {
      display: flex;
      align-items: center;
      gap: var(--spacing-8, 8px);
      font-size: 13px;
    }

    .bundle-version {
      font-family: monospace;
      font-weight: 500;
      color: var(--colors-text-brand-900, #002266);
    }

    .bundle-compat,
    .bundle-count {
      font-size: 11px;
      color: var(--colors-foreground-fg-gray-500, #98a2b3);
    }

    .bundle-compat {
      font-family: monospace;
    }

    .member-row {
      display: flex;
      align-items: center;
      gap: var(--spacing-8, 8px);
      font-size: 12px;
    }

    .member-name {
      font-family: monospace;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
    }

    .member-version {
      font-family: monospace;
      color: var(--colors-text-brand-900, #002266);
    }

    /* The product MFE node stays open and can't be collapsed: hide its chevron. */
    bee-folder-tree-node.locked-open::part(expand-button) {
      visibility: hidden;
      pointer-events: none;
    }

    .empty {
      padding: var(--spacing-24, 24px);
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      text-align: center;
      font-size: 13px;
    }

    .error-text {
      font-size: 12px;
      color: var(--colors-foreground-fg-error-600, #d92d20);
    }

  `;

  @property({ attribute: false }) activeProductFilter: string | null = null;
  @property({ attribute: false }) products: ProductsResponse | null = null;
  @property({ attribute: false }) environments: EnvironmentsResponse | null = null;

  @state() private _tenants: TreeTenant[] = [];
  @state() private _loading = true;
  @state() private _error = "";

  // Dialog state
  @state() private _showCreateTenant = false;
  @state() private _createInstanceCtx: { tenant: string } | null = null;
  @state() private _createPlmProductCtx: { tenant: string; instance: string } | null = null;
  @state() private _createBundleCtx: { tenant: string; instance: string; product: string; plmVersions: string[] } | null = null;
  @state() private _editPlmProductCtx: { tenant: string; instance: string; product: PlmProductInfo } | null = null;
  @state() private _deleteCtx: DeleteCtx | null = null;
  // Bundle tag most recently copied to the clipboard (drives a transient icon swap).
  @state() private _copiedTag: string | null = null;

  async connectedCallback() {
    super.connectedCallback();
    await this._loadTenants();
  }

  private async _loadTenants() {
    this._loading = true;
    this._error = "";
    try {
      const { tenants } = await api.getTenants();
      const tree: TreeTenant[] = await Promise.all(
        tenants.map(async (info): Promise<TreeTenant> => {
          let instances: TreeInstance[] | undefined;
          let instancesError: string | undefined;
          try {
            const res = await api.getTenantInstances(info.name);
            instances = await Promise.all(
              res.instances.map(async (iInfo): Promise<TreeInstance> => {
                let products: TreePlmProduct[] | undefined;
                let productsError: string | undefined;
                try {
                  const pres = await api.getPlmProducts(info.name, iInfo.name);
                  products = pres.products.map((pInfo) => ({ info: pInfo }));
                } catch (pe: unknown) {
                  productsError = pe instanceof Error ? pe.message : "Failed to load products";
                }
                return { info: iInfo, products, productsError };
              })
            );
          } catch (ie: unknown) {
            instancesError = ie instanceof Error ? ie.message : "Failed to load instances";
          }
          return { info, instances, instancesError };
        })
      );
      this._tenants = tree;
    } catch (e: unknown) {
      this._error = e instanceof Error ? e.message : "Failed to load tenants";
    } finally {
      this._loading = false;
    }
  }

  private async _loadInstances(tenantIdx: number) {
    const tenant = this._tenants[tenantIdx];
    if (!tenant || tenant.loadingInstances) return;
    const tenantName = tenant.info.name;
    // The tree can change across the await; re-check identity before applying
    // the result so a shifted index can't patch the wrong tenant.
    const stillMatches = () => this._tenants[tenantIdx]?.info.name === tenantName;

    this._tenants = patchTenant(this._tenants, tenantIdx, { loadingInstances: true, instancesError: undefined });

    try {
      const { instances } = await api.getTenantInstances(tenantName);
      if (!stillMatches()) return;
      this._tenants = patchTenant(this._tenants, tenantIdx, {
        loadingInstances: false,
        instances: instances.map((info) => ({ info })),
      });
    } catch (e: unknown) {
      if (!stillMatches()) return;
      this._tenants = patchTenant(this._tenants, tenantIdx, {
        loadingInstances: false,
        instancesError: e instanceof Error ? e.message : "Failed to load instances",
      });
    }
  }

  private async _loadPlmProducts(tenantIdx: number, instanceIdx: number) {
    const tenant = this._tenants[tenantIdx];
    const instance = tenant?.instances?.[instanceIdx];
    if (!instance || instance.loadingProducts) return;
    // Capture names up front and re-check identity after the await: the tree can
    // change while the request is in flight (e.g. a tenant deleted), so an
    // index-based patch could otherwise hit the wrong node.
    const tenantName = tenant.info.name;
    const instanceName = instance.info.name;
    const stillMatches = () => {
      const t = this._tenants[tenantIdx];
      return t?.info.name === tenantName && t.instances?.[instanceIdx]?.info.name === instanceName;
    };

    this._tenants = patchInstance(this._tenants, tenantIdx, instanceIdx, { loadingProducts: true, productsError: undefined });

    try {
      const { products } = await api.getPlmProducts(tenantName, instanceName);
      if (!stillMatches()) return;
      this._tenants = patchInstance(this._tenants, tenantIdx, instanceIdx, {
        loadingProducts: false,
        products: products.map((info) => ({ info })),
      });
    } catch (e: unknown) {
      if (!stillMatches()) return;
      this._tenants = patchInstance(this._tenants, tenantIdx, instanceIdx, {
        loadingProducts: false,
        productsError: e instanceof Error ? e.message : "Failed to load products",
      });
    }
  }

  private async _loadBundles(tenantIdx: number, instanceIdx: number, productIdx: number) {
    const tenant = this._tenants[tenantIdx];
    const instance = tenant?.instances?.[instanceIdx];
    const plmProduct = instance?.products?.[productIdx];
    if (!plmProduct || plmProduct.loadingBundles) return;
    // Capture names and re-check identity after the await so a tree change in
    // flight (deleted/shifted node) can't patch the wrong product.
    const tenantName = tenant.info.name;
    const instanceName = instance.info.name;
    const productName = plmProduct.info.name;
    const stillMatches = () => {
      const inst = this._tenants[tenantIdx]?.instances?.[instanceIdx];
      return this._tenants[tenantIdx]?.info.name === tenantName
        && inst?.info.name === instanceName
        && inst.products?.[productIdx]?.info.name === productName;
    };

    this._tenants = patchPlmProduct(this._tenants, tenantIdx, instanceIdx, productIdx, { loadingBundles: true, bundlesError: undefined });

    try {
      const { bundles } = await api.getInstanceBundles({
        tenant: tenantName,
        instance: instanceName,
        product: productName,
      });
      if (!stillMatches()) return;
      this._tenants = patchPlmProduct(this._tenants, tenantIdx, instanceIdx, productIdx, { loadingBundles: false, bundles });
    } catch (e: unknown) {
      if (!stillMatches()) return;
      this._tenants = patchPlmProduct(this._tenants, tenantIdx, instanceIdx, productIdx, {
        loadingBundles: false,
        bundlesError: e instanceof Error ? e.message : "Failed to load bundles",
      });
    }
  }

  private _handleLazyLoad(e: Event) {
    // bee-lazy-load bubbles (composed) from the node; e.target is retargeted to
    // the bee-folder-tree-node, which carries the data-*-idx attributes.
    const item = e.target as HTMLElement | null;
    if (!item || !item.dataset) return;

    const tenantIdx = Number(item.dataset.tenantIdx);
    const instanceIdx = item.dataset.instanceIdx !== undefined ? Number(item.dataset.instanceIdx) : undefined;
    const productIdx = item.dataset.productIdx !== undefined ? Number(item.dataset.productIdx) : undefined;

    if (productIdx !== undefined && instanceIdx !== undefined) {
      void this._loadBundles(tenantIdx, instanceIdx, productIdx);
    } else if (instanceIdx !== undefined) {
      void this._loadPlmProducts(tenantIdx, instanceIdx);
    } else {
      void this._loadInstances(tenantIdx);
    }
  }

  private _getActiveProductFilter(): number {
    if (!this.activeProductFilter || !this.products) return 0;
    const names = Object.keys(this.products.products).sort();
    const idx = names.indexOf(this.activeProductFilter);
    return idx >= 0 ? idx : 0;
  }

  render() {
    if (this._loading) {
      return html`<div class="empty"><bee-spinner size="medium" type="blue"></bee-spinner></div>`;
    }
    if (this._error) {
      return html`<bee-alert variant="error" open><span slot="message">${this._error}</span></bee-alert>`;
    }

    return html`
      <div class="controls">
        <bee-button label="Refresh" bee-type="secondary-grey" size="small" icon="refresh" @click=${() => this._loadTenants()}></bee-button>
        <bee-button label="+ Add Tenant" bee-type="primary" size="small" @click=${() => { this._showCreateTenant = true; }}></bee-button>
      </div>

      <div class="tree-wrap">
        ${this._tenants.length === 0
          ? html`<div class="empty">No tenants configured</div>`
          : html`
            <bee-folder-tree selection="single" @bee-lazy-load=${this._handleLazyLoad}>
              ${this._tenants.map((tenant, ci) => this._renderTenant(tenant, ci))}
            </bee-folder-tree>
          `}
      </div>

      ${this._renderDialogs()}
    `;
  }

  private _renderDialogs() {
    return html`
      <create-tenant-dialog
        ?open=${this._showCreateTenant}
        @close=${() => { this._showCreateTenant = false; }}
        @tenant-created=${(e: CustomEvent) => this._onTenantCreated(e)}
      ></create-tenant-dialog>

      <create-instance-dialog
        ?open=${!!this._createInstanceCtx}
        tenant=${this._createInstanceCtx?.tenant ?? ""}
        @close=${() => { this._createInstanceCtx = null; }}
        @instance-created=${(e: CustomEvent) => this._onInstanceCreated(e)}
      ></create-instance-dialog>

      <create-plm-product-dialog
        ?open=${!!this._createPlmProductCtx}
        tenant=${this._createPlmProductCtx?.tenant ?? ""}
        instance=${this._createPlmProductCtx?.instance ?? ""}
        @close=${() => { this._createPlmProductCtx = null; }}
        @plm-product-created=${(e: CustomEvent) => this._onPlmProductCreated(e)}
      ></create-plm-product-dialog>

      <create-bundle-dialog
        ?open=${!!this._createBundleCtx}
        tenant=${this._createBundleCtx?.tenant ?? ""}
        instance=${this._createBundleCtx?.instance ?? ""}
        product=${this._createBundleCtx?.product ?? ""}
        .plmVersions=${this._createBundleCtx?.plmVersions ?? []}
        .activeProductFilter=${this._getActiveProductFilter()}
        .products=${this.products}
        .environments=${this.environments}
        @close=${() => { this._createBundleCtx = null; }}
      ></create-bundle-dialog>

      <edit-plm-product-dialog
        ?open=${!!this._editPlmProductCtx}
        tenant=${this._editPlmProductCtx?.tenant ?? ""}
        instance=${this._editPlmProductCtx?.instance ?? ""}
        name=${this._editPlmProductCtx?.product.name ?? ""}
        hostname=${this._editPlmProductCtx?.product.hostname ?? ""}
        .versions=${this._editPlmProductCtx?.product.versions ?? []}
        @close=${() => { this._editPlmProductCtx = null; }}
        @plm-product-updated=${(e: CustomEvent) => this._onPlmProductUpdated(e)}
      ></edit-plm-product-dialog>

      <confirm-delete-dialog
        ?open=${!!this._deleteCtx}
        entityType=${this._deleteCtx?.entityType ?? ""}
        name=${this._deleteCtx?.name ?? ""}
        hint=${this._deleteCtx?.hint ?? ""}
        .action=${this._deleteCtx?.action ?? (async () => {})}
        @close=${() => { this._deleteCtx = null; }}
        @deleted=${() => this._onDeleted()}
      ></confirm-delete-dialog>
    `;
  }

  private _renderTenant(tenant: TreeTenant, ci: number) {
    const hasInstances = !!tenant.instances;
    return html`
      <bee-folder-tree-node expanded data-tenant-idx=${ci} .loading=${!!tenant.loadingInstances}>
        <bee-icon slot="prefix-icon" class="level-icon" type=${LEVEL_ICONS.tenant}></bee-icon>
        <span class="node-name mono">${tenant.info.name}</span>
        <span slot="actions" class="node-actions">
          <bee-icon-button icon="plus" size="small" label="Add instance"
            @click=${(e: Event) => { e.stopPropagation(); this._createInstanceCtx = { tenant: tenant.info.name }; }}
          ></bee-icon-button>
          <bee-icon-button icon="trash" size="small" label="Delete tenant"
            @click=${(e: Event) => { e.stopPropagation(); this._confirmDeleteTenant(tenant); }}
          ></bee-icon-button>
        </span>
          ${tenant.instancesError ? html`<bee-folder-tree-node .isLeaf=${true}><span class="error-text">${tenant.instancesError}</span></bee-folder-tree-node>` : nothing}
          ${hasInstances && !tenant.instancesError && tenant.instances!.length === 0
            ? html`<bee-folder-tree-node .isLeaf=${true}><span class="node-meta">No instances</span></bee-folder-tree-node>`
            : nothing}
          ${tenant.instances?.map((inst, ii) => this._renderInstance(inst, ci, ii))}
      </bee-folder-tree-node>
    `;
  }

  private _renderInstance(inst: TreeInstance, ci: number, ii: number) {
    const hasProducts = !!inst.products;
    return html`
      <bee-folder-tree-node expanded data-tenant-idx=${ci} data-instance-idx=${ii} .loading=${!!inst.loadingProducts}>
        <bee-icon slot="prefix-icon" class="level-icon" type=${LEVEL_ICONS.instance}></bee-icon>
        <span class="node-name mono">${inst.info.name}</span>
        <span slot="actions" class="node-actions">
          <bee-icon-button icon="plus" size="small" label="Add PLM product"
            @click=${(e: Event) => {
              e.stopPropagation();
              this._createPlmProductCtx = { tenant: this._tenants[ci].info.name, instance: inst.info.name };
            }}
          ></bee-icon-button>
          <bee-icon-button icon="trash" size="small" label="Delete instance"
            @click=${(e: Event) => { e.stopPropagation(); this._confirmDeleteInstance(ci, inst); }}
          ></bee-icon-button>
        </span>
          ${inst.productsError ? html`<bee-folder-tree-node .isLeaf=${true}><span class="error-text">${inst.productsError}</span></bee-folder-tree-node>` : nothing}
          ${hasProducts && !inst.productsError && inst.products!.length === 0
            ? html`<bee-folder-tree-node .isLeaf=${true}><span class="node-meta">No PLM products</span></bee-folder-tree-node>`
            : nothing}
          ${inst.products?.map((p, pi) => this._renderPlmProduct(p, ci, ii, pi))}
      </bee-folder-tree-node>
    `;
  }

  private _renderPlmProduct(plmProduct: TreePlmProduct, ci: number, ii: number, pi: number) {
    const hasBundles = !!plmProduct.bundles;
    const visibleBundles = this._visibleBundles(plmProduct);
    // Distinguish "product has no bundles" from "all bundles filtered out by the
    // active product" so the empty-state copy is accurate.
    const filteredOut = hasBundles && plmProduct.bundles!.length > 0 && visibleBundles.length === 0;
    return html`
      <bee-folder-tree-node
        ?lazy=${!hasBundles}
        data-tenant-idx=${ci}
        data-instance-idx=${ii}
        data-product-idx=${pi}
        .loading=${!!plmProduct.loadingBundles}
      >
        <bee-icon slot="prefix-icon" class="level-icon" type=${LEVEL_ICONS.product}></bee-icon>
        <span class="node-name">${plmProduct.info.name}</span>
        ${plmProduct.info.hostname ? html`<span class="node-meta">${plmProduct.info.hostname}</span>` : nothing}
        <span slot="actions" class="node-actions">
          <bee-icon-button icon="plus" size="small" label="Create bundle"
            @click=${(e: Event) => { e.stopPropagation(); this._openCreateBundle(ci, ii, plmProduct); }}
          ></bee-icon-button>
          <bee-icon-button icon="edit" size="small" label="Edit PLM product"
            @click=${(e: Event) => {
              e.stopPropagation();
              const inst = this._tenants[ci].instances?.[ii];
              if (inst) this._editPlmProductCtx = { tenant: this._tenants[ci].info.name, instance: inst.info.name, product: plmProduct.info };
            }}
          ></bee-icon-button>
          <bee-icon-button icon="trash" size="small" label="Delete PLM product"
            @click=${(e: Event) => { e.stopPropagation(); this._confirmDeletePlmProduct(ci, ii, plmProduct); }}
          ></bee-icon-button>
        </span>
          ${plmProduct.bundlesError ? html`<bee-folder-tree-node .isLeaf=${true}><span class="error-text">${plmProduct.bundlesError}</span></bee-folder-tree-node>` : nothing}
          ${hasBundles && !plmProduct.bundlesError && visibleBundles.length === 0
            ? html`<bee-folder-tree-node .isLeaf=${true}><span class="node-meta">${filteredOut ? "No bundles for the selected product" : "No bundles"}</span></bee-folder-tree-node>`
            : nothing}
          ${visibleBundles.map((b) => this._renderBundle(b, ci, ii, pi))}
      </bee-folder-tree-node>
    `;
  }

  private _renderBundle(bundle: BundleSummary, ci: number, ii: number, pi: number) {
    const members = bundle.members ?? [];
    const productMember = members.find((m) => m.isProduct);
    const childMembers = members.filter((m) => !m.isProduct);
    const copied = this._copiedTag === bundle.version;
    const meta = `${bundle.memberCount} member${bundle.memberCount !== 1 ? "s" : ""}${bundle.createdAt ? ` - ${this._formatDate(bundle.createdAt)}` : ""}`;
    return html`
      <bee-folder-tree-node .isLeaf=${members.length === 0}>
        <bee-icon slot="prefix-icon" class="level-icon" type=${LEVEL_ICONS.bundle}></bee-icon>
        <span class="bundle-leaf">
          <span class="bundle-version">${bundle.version}</span>
          ${bundle.compat?.version ? html`<span class="bundle-compat">compat: ${bundle.compat.version}</span>` : nothing}
          <span class="bundle-count">${meta}</span>
        </span>
        <span slot="actions" class="node-actions">
          <bee-icon-button
            icon=${copied ? "clipboard-check" : "copy"}
            size="small"
            label=${copied ? "Copied" : "Copy bundle tag"}
            @click=${(e: Event) => { e.stopPropagation(); void this._copyTag(bundle.version); }}
          ></bee-icon-button>
          <bee-icon-button icon="trash" size="small" label="Delete bundle"
            @click=${(e: Event) => { e.stopPropagation(); this._confirmDeleteBundle(ci, ii, pi, bundle); }}
          ></bee-icon-button>
        </span>
        ${productMember
          ? this._renderMemberNode(productMember, childMembers.map((m) => this._renderMemberNode(m)))
          : childMembers.map((m) => this._renderMemberNode(m))}
      </bee-folder-tree-node>
    `;
  }

  // A bundle member row. The product MFE nests its sibling MFEs as children and
  // is locked open (chevron hidden, not collapsible); child MFEs are leaves.
  private _renderMemberNode(m: BundleMember, children: unknown[] = []) {
    const isProduct = !!m.isProduct;
    const hasChildren = children.length > 0;
    return html`
      <bee-folder-tree-node
        class=${isProduct && hasChildren ? "locked-open" : ""}
        ?expanded=${isProduct && hasChildren}
        .isLeaf=${!hasChildren}
      >
        <bee-icon slot="prefix-icon" class="level-icon" type=${isProduct ? "layout" : "arrow-right"}></bee-icon>
        <span class="member-row">
          <span class="member-name">${m.name}</span>
          <span class="member-version">${m.version || "—"}</span>
        </span>
        ${children}
      </bee-folder-tree-node>
    `;
  }

  private async _copyTag(tag: string) {
    try {
      await navigator.clipboard.writeText(tag);
      this._copiedTag = tag;
      // Revert the icon after a short confirmation window.
      setTimeout(() => { if (this._copiedTag === tag) this._copiedTag = null; }, 1500);
    } catch {
      // Clipboard API may be unavailable (e.g. insecure context); nothing to do.
    }
  }

  // Bundles shown under a product are scoped to the globally-selected HAWAII
  // product. Bundles created before product stamping have no hawaiiProduct, so
  // they stay visible rather than disappear.
  private _visibleBundles(plmProduct: TreePlmProduct): BundleSummary[] {
    const all = plmProduct.bundles ?? [];
    const active = this.activeProductFilter;
    if (!active) return all;
    return all.filter((b) => !b.hawaiiProduct || b.hawaiiProduct === active);
  }

  private _formatDate(iso: string): string {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
  }

  private _openCreateBundle(ci: number, ii: number, plmProduct: TreePlmProduct) {
    const inst = this._tenants[ci].instances?.[ii];
    if (!inst) return;
    this._createBundleCtx = {
      tenant: this._tenants[ci].info.name,
      instance: inst.info.name,
      product: plmProduct.info.name,
      plmVersions: plmProduct.info.versions,
    };
  }

  // --- Delete confirmation context builders ---

  private _confirmDeleteTenant(tenant: TreeTenant) {
    const name = tenant.info.name;
    this._deleteCtx = {
      entityType: "tenant",
      name,
      action: async () => { await api.deleteTenant(name); },
      onDeleted: () => { this._tenants = removeTenant(this._tenants, name); },
    };
  }

  private _confirmDeleteInstance(ci: number, inst: TreeInstance) {
    const tenant = this._tenants[ci].info.name;
    const name = inst.info.name;
    this._deleteCtx = {
      entityType: "instance",
      name,
      action: async () => { await api.deleteInstance(tenant, name); },
      onDeleted: () => { this._tenants = removeInstance(this._tenants, tenant, name); },
    };
  }

  private _confirmDeletePlmProduct(ci: number, ii: number, plmProduct: TreePlmProduct) {
    const tenant = this._tenants[ci].info.name;
    const instance = this._tenants[ci].instances![ii].info.name;
    const name = plmProduct.info.name;
    this._deleteCtx = {
      entityType: "PLM product",
      name,
      action: async () => { await api.deletePlmProduct(tenant, instance, name); },
      onDeleted: () => { this._tenants = removePlmProduct(this._tenants, tenant, instance, name); },
    };
  }

  private _confirmDeleteBundle(ci: number, ii: number, pi: number, bundle: BundleSummary) {
    const tenant = this._tenants[ci].info.name;
    const instance = this._tenants[ci].instances![ii].info.name;
    const product = this._tenants[ci].instances![ii].products![pi].info.name;
    this._deleteCtx = {
      entityType: "bundle",
      name: bundle.version,
      hint: "Bundle removal is applied via a pull request and takes effect once merged.",
      action: () => api.deleteInstanceBundle({ tenant, instance, product, version: bundle.version }),
      onDeleted: () => {},
    };
  }

  private _onDeleted() {
    this._deleteCtx?.onDeleted();
    this._deleteCtx = null;
  }

  // KV is eventually consistent (its list index lags a successful put by a few
  // seconds), so we optimistically insert the just-created entity from the POST
  // response instead of re-fetching — a re-fetch would briefly drop the new row.
  private _onTenantCreated(e: CustomEvent) {
    this._showCreateTenant = false;
    const name = e.detail?.name;
    if (!name || this._tenants.some((t) => t.info.name === name)) return;
    this._tenants = [...this._tenants, { info: { name } }].sort((a, b) =>
      a.info.name.localeCompare(b.info.name)
    );
  }

  private _onInstanceCreated(e: CustomEvent) {
    const ctx = this._createInstanceCtx;
    this._createInstanceCtx = null;
    const name = e.detail?.name;
    if (!ctx || !name) return;
    this._tenants = this._tenants.map((c) => {
      if (c.info.name !== ctx.tenant) return c;
      // A freshly-created tenant has no loaded instances yet and no error — it's
      // genuinely empty, so seed the list with the new instance. On a load error
      // we leave it undefined so expanding can retry rather than show a partial list.
      if (!c.instances) {
        return c.instancesError ? c : { ...c, instances: [{ info: { name } }] };
      }
      if (c.instances.some((inst) => inst.info.name === name)) return c;
      const instances = [...c.instances, { info: { name } }].sort(
        (a, b) => a.info.name.localeCompare(b.info.name)
      );
      return { ...c, instances };
    });
  }

  private _onPlmProductCreated(e: CustomEvent) {
    const ctx = this._createPlmProductCtx;
    this._createPlmProductCtx = null;
    const name = e.detail?.name;
    if (!ctx || !name) return;
    this._tenants = this._tenants.map((c) => {
      if (c.info.name !== ctx.tenant) return c;
      return {
        ...c,
        instances: c.instances?.map((inst) => {
          if (inst.info.name !== ctx.instance) return inst;
          // A freshly-created instance has no loaded products yet and no error —
          // seed the list with the new product. On a load error, leave it for retry.
          if (!inst.products) {
            return inst.productsError ? inst : { ...inst, products: [{ info: { name, hostname: "", versions: [] } }] };
          }
          if (inst.products.some((p) => p.info.name === name)) return inst;
          const products = [...inst.products, { info: { name, hostname: "", versions: [] } }].sort((a, b) =>
            a.info.name.localeCompare(b.info.name)
          );
          return { ...inst, products };
        }),
      };
    });
  }

  private _onPlmProductUpdated(e: CustomEvent) {
    const ctx = this._editPlmProductCtx;
    this._editPlmProductCtx = null;
    const product = e.detail as PlmProductInfo | undefined;
    if (!ctx || !product) return;
    this._tenants = this._tenants.map((c) => {
      if (c.info.name !== ctx.tenant) return c;
      return {
        ...c,
        instances: c.instances?.map((inst) => {
          if (inst.info.name !== ctx.instance || !inst.products) return inst;
          return {
            ...inst,
            products: inst.products.map((p) => (p.info.name === product.name ? { ...p, info: product } : p)),
          };
        }),
      };
    });
  }
}
